import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '.';
import { CustomError } from 'src/lib/error';
import logger from 'src/logger';
import { eventBus } from 'src/core/event-bus';
import { miniSerializeErrorOverride } from './serialize-error';
import { createCelloToken, getRewardDetails } from 'src/services/referral/referral.service';

// Initial state for the slice
const initialState = {
  rewardDetails: null, // This will store the reward details
};

// Create the slice
const referralSlice = createSlice({
  name: 'referral',
  initialState,
  reducers: {
    setRewardDetails: (state, action: PayloadAction<any>) => {
      state.rewardDetails = action.payload;
    },
  },
});

export const { setRewardDetails } = referralSlice.actions;

// Thunk to generate Cello token
export const generateCelloTokenThunk = createAsyncThunk(
  'post/generateCelloToken',
  async (_, { getState }) => {
    const { session } = getState() as RootState;
    try {
      if (!session) throw new CustomError({ errorCode: 'SessionNotFound' });
      return await createCelloToken();
    } catch (err) {
      logger.reportError(err, { message: 'Error in generateCelloTokenThunk' });
      eventBus.emit('error/showToast', err);
      throw err;
    }
  },
  {
    serializeError: miniSerializeErrorOverride,
  },
);

// Thunk to fetch referral reward details and directly dispatch to the store
export const getReferralRewardDetailsThunk = createAsyncThunk<void, void, { state: RootState }>(
  'get/fetchCelloRewardDetails',
  async (_, { getState, dispatch }) => {
    const { referral } = getState() as RootState;

    // Check if data already exists in store
    if (referral.rewardDetails) {
      // Data already exists, so do not make the API call
      return;
    }

    try {
      const rewardDetails = await getRewardDetails();
      dispatch(setRewardDetails(rewardDetails));
    } catch (err) {
      logger.reportError(err, { message: 'Error in getReferralRewardDetailsThunk' });
      eventBus.emit('error/showToast', err);
      throw err;
    }
  },
  {
    serializeError: miniSerializeErrorOverride,
  },
);

// Selector to get the rewardDetails from the store
export const selectReferralRewardDetails = (state: RootState) => state.referral.rewardDetails;

export default referralSlice.reducer;
